/* ── Lobby (admin) ─────────────────────────────────────────────────────────── */
.lobby {
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* Sticky nav: fades from solid black to transparent over the billboard. */
.nav {
  position: sticky;
  top: 0;
  z-index: var(--z-popover);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  flex-wrap: wrap;
  padding: var(--space-4) var(--space-7);
  background: linear-gradient(180deg, rgba(10, 10, 10, 0.95), rgba(10, 10, 10, 0));
}

.navLeft,
.navRight {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.navRight {
  flex-wrap: wrap;
  gap: var(--space-3);
}

.navTag {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-sm);
  padding: 2px 6px;
}

.joinForm {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.joinInput {
  width: 160px;
  height: 36px;
  font-size: var(--fs-sm);
  background: rgba(0, 0, 0, 0.6);
}

.userChip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--fs-sm);
  font-weight: 500;
  color: var(--color-text);
  max-width: 180px;
}

.avatar {
  width: 28px;
  height: 28px;
  border-radius: var(--radius-sm);
  background: var(--color-accent);
  color: #fff;
  font-weight: 800;
  font-size: var(--fs-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.userName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Billboard ─────────────────────────────────────────────────────────────── */
.hero {
  position: relative;
  margin-top: -68px; /* tuck under the sticky nav */
  min-height: 380px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  background:
    radial-gradient(ellipse 70% 80% at 15% 0%, rgba(229, 9, 20, 0.28), transparent 70%),
    var(--color-bg);
}

.heroBackdrop {
  position: absolute;
  inset: -40px;
  width: calc(100% + 80px);
  height: calc(100% + 80px);
  object-fit: cover;
  object-position: center 20%;
  filter: blur(36px) saturate(1.2);
  opacity: 0.5;
  animation: sr-rise 260ms var(--ease-out);
}

.heroShade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(10, 10, 10, 0.92) 0%, rgba(10, 10, 10, 0.45) 55%, rgba(10, 10, 10, 0.1) 100%),
    linear-gradient(180deg, transparent 50%, var(--color-bg) 100%);
}

.heroBody {
  position: relative;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 104px var(--space-7) var(--space-8);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
  animation: sr-rise 200ms var(--ease-out);
}

.heroKicker {
  font-size: var(--fs-xs);
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-accent-text);
}

.heroTitle {
  font-size: clamp(34px, 5.5vw, 64px);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.02;
  max-width: 18ch;
  text-wrap: balance;
}

.heroMeta {
  color: var(--color-text-muted);
  font-size: var(--fs-md);
  font-variant-numeric: tabular-nums;
}

.heroHint {
  color: var(--color-text-muted);
  font-size: var(--fs-lg);
  max-width: 44ch;
}

.heroForm {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: center;
  margin-top: var(--space-3);
  width: 100%;
  max-width: 720px;
}

.heroInput {
  flex: 1 1 260px;
  background: rgba(0, 0, 0, 0.55);
}

.heroCta {
  height: 46px;
  font-size: var(--fs-md);
}

/* ── Invitation code display ──────────────────────────────────────────────── */
.invitationCodeBox {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: var(--space-3);
  padding: 12px 16px;
  border-left: 3px solid var(--color-ok);
  border-radius: var(--radius-sm);
  background: rgba(0, 0, 0, 0.6);
}

.invitationCodeLabel {
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
}

.invitationCodeValue {
  font-family: var(--font-mono);
  font-size: var(--fs-xl);
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-ok);
  user-select: all;
}

/* ── Content below the billboard ──────────────────────────────────────────── */
.content {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: var(--space-4) var(--space-7) 72px;
  display: flex;
  flex-direction: column;
  gap: var(--space-9);
}

/* ── Active rooms ─────────────────────────────────────────────────────────── */
.activeRooms {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
  gap: var(--space-3);
}

.activeRoom {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-4);
  border-radius: var(--radius-md);
  background: var(--color-bg-surface);
  transition: transform 80ms var(--ease-out), background var(--motion-fast) var(--ease-out);
}

.activeRoom:hover {
  background: var(--color-bg-surface-hover);
  transform: translateY(-2px);
}

.activeRoomInfo {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.liveTag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-accent-text);
}

.activeRoomDot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-accent);
  animation: sr-pulse 1.6s ease-in-out infinite;
}

.activeRoomName {
  font-weight: 700;
  font-size: var(--fs-lg);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activeRoomMeta {
  font-size: var(--fs-xs);
  color: var(--color-text-muted);
}

.activeRoomActions {
  display: flex;
  gap: var(--space-2);
}

.hostKeyValue {
  font-family: var(--font-mono, monospace);
  color: var(--color-text);
  user-select: all;
}

.hostKeyCopy {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: var(--color-accent-text);
  cursor: pointer;
  text-decoration: underline;
}

@media (max-width: 720px) {
  /* Nav: brand + account on row one, join-by-code on its own full-width row. */
  .nav {
    padding: var(--space-3) var(--space-4);
    padding-top: calc(var(--space-3) + env(safe-area-inset-top));
    gap: var(--space-3);
    background: rgba(10, 10, 10, 0.96);
    position: sticky;
  }
  .navLeft {
    flex: 1 1 auto;
  }
  .navRight {
    display: contents;
  }
  .joinForm {
    order: 3;
    flex: 1 1 100%;
  }
  .joinInput {
    flex: 1;
    width: auto;
    height: 44px;
  }
  .userName {
    display: none;
  }

  .hero {
    margin-top: 0;
    min-height: 0;
  }
  .heroBody {
    padding: var(--space-8) var(--space-4) var(--space-6);
  }
  .heroForm {
    flex-direction: column;
    align-items: stretch;
  }
  .heroInput {
    flex: none;
  }
  .heroCta {
    width: 100%;
    height: 50px;
  }
  .content {
    padding: var(--space-2) var(--space-4) calc(var(--space-9) + env(safe-area-inset-bottom));
    gap: var(--space-7);
  }
  .activeRooms {
    grid-template-columns: minmax(0, 1fr);
  }
}
